import React from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Icon, IconLink } from '@sg/garnish';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { PaymentMethod } from '../../machine/scan-to-pay.machine.types';
import {
  PAYMENT_METHOD_ICON_MAP,
  iconBackgroundPalettes,
  iconPalettes,
  ScanToPayPalette,
  textPalettes,
  lightIconPalettes,
} from '../../utils';

export const ScanToPayPaymentMethod = (props: ScanToPayPaymentMethodProps) => {
  const { paymentMethodId, paymentMethods, palette, onPaymentMethodSelection } =
    props;

  const paymentMethod = paymentMethods.find(
    (paymentMethod) => paymentMethod.id === paymentMethodId,
  );

  const { formatMessage } = useIntl();
  const a11yLabel = formatMessage(messages.a11yLabel);

  if (!paymentMethod) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.leftSide}>
        <Icon
          color={iconPalettes[palette]}
          name={PAYMENT_METHOD_ICON_MAP[paymentMethod.cardType]}
          width={32}
          height={32}
        />

        <View>
          <BodyText sizeMatch={['14']} style={textPalettes[palette]}>
            <FormattedMessage {...messages.title} />
          </BodyText>

          <BodyText bold sizeMatch={['16']} style={textPalettes[palette]}>
            {paymentMethod.nickname ?? paymentMethod.description}
          </BodyText>
        </View>
      </View>

      <IconLink
        name="IconList"
        width={24}
        height={24}
        accessibilityLabel={a11yLabel}
        color={lightIconPalettes[palette]}
        style={[styles.cta, iconBackgroundPalettes[palette]]}
        onPress={onPaymentMethodSelection}
      />
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Payment method',
    description: 'Scan To Pay | Payment method | Title',
  },
  a11yLabel: {
    defaultMessage: 'Change payment method',
    description: 'Scan To Pay | Payment method | Accessibility Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  leftSide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  cta: {
    width: 40,
    height: 40,
    borderRadius: 40,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayPaymentMethodProps = {
  paymentMethodId: string;
  paymentMethods: readonly PaymentMethod[];
  palette: ScanToPayPalette;
  onPaymentMethodSelection: () => void;
};
